import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft,
  Clock3,
  Copy,
  DollarSign,
  History,
  MousePointerClick,
  Save,
  Settings,
  ShieldCheck,
  TrendingUp,
  Users,
  Wallet,
} from 'lucide-react';
import { API, toast } from '@/App';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  formatAffiliateCurrency,
  formatAffiliateDate,
  formatAffiliateDateTime,
  getAffiliateBackLabel,
  getAffiliateBackPath,
  getAffiliateStatusLabel,
  getAffiliateStatusToneClasses,
  getAffiliateTypeLabel,
  getPayoutStatusLabel,
  getPayoutStatusVariant,
  normalizeAffiliateType,
} from '@/utils/affiliateWorkspace';

const DEFAULT_PAYMENT_PROFILE = {
  preferred_payout_method: '',
  mobile_money_number: '',
  mobile_money_operator: '',
  bank_account_name: '',
  bank_account_number: '',
  bank_name: '',
};

const INVITATION_LINK_LABELS = {
  default: 'Lien principal',
  vendor: 'Lien vendeurs directs',
  ambassador: 'Lien recrutement ambassadeurs',
};

const NETWORK_RELATIONSHIP_LABELS = {
  ambassador_direct: 'Ambassadeur recrute',
  vendor_direct: 'Vendeur direct',
  vendor_indirect: 'Vendeur indirect',
};

const NETWORK_TARGET_LABELS = {
  vendor: 'Vendeur',
  ambassador: 'Ambassadeur',
};

const resolvePayoutRequestErrorMessage = (error) => {
  const detail = String(error?.response?.data?.detail || '').toLowerCase();
  const status = Number(error?.response?.status || 0);
  if (detail.includes('already') || detail.includes('deja') || status === 409) {
    return 'Une demande de paiement est deja en attente de traitement.';
  }
  if (detail.includes('minimum payout amount') || detail.includes('minimum')) {
    return 'Le montant est inferieur au minimum autorise.';
  }
  if (
    detail.includes('available affiliate balance') ||
    detail.includes('pending affiliate balance') ||
    detail.includes('exceeds')
  ) {
    return 'Montant insuffisant par rapport aux commissions disponibles.';
  }
  if (status >= 500) {
    return 'Erreur serveur lors de la creation de la demande.';
  }
  return "Impossible d'envoyer la demande de paiement.";
};

const MetricCard = ({ title, value, helper, icon: Icon, emphasis = 'text-gray-900' }) => (
  <Card className="border-slate-200">
    <CardHeader className="pb-2">
      <CardDescription>{title}</CardDescription>
      <CardTitle className={`text-2xl ${emphasis}`}>{value}</CardTitle>
    </CardHeader>
    <CardContent className="flex items-center justify-between text-sm text-slate-500">
      <span>{helper || ' '}</span>
      {Icon ? <Icon className="h-5 w-5" /> : null}
    </CardContent>
  </Card>
);

const StatusPill = ({ statusCode }) => (
  <span
    className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium ${getAffiliateStatusToneClasses(statusCode)}`}
  >
    {getAffiliateStatusLabel(statusCode)}
  </span>
);

const SectionHeading = ({ title, description }) => (
  <div className="space-y-1">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    {description ? <p className="text-sm text-slate-500">{description}</p> : null}
  </div>
);

const buildInvitationLinkItems = (invitation) => {
  const items = [];
  const seen = new Set();
  const links = invitation?.links && typeof invitation.links === 'object' ? invitation.links : {};

  Object.entries(links).forEach(([key, value]) => {
    const link = String(value || '').trim();
    if (!link || seen.has(link)) {
      return;
    }
    seen.add(link);
    items.push({
      key,
      label: INVITATION_LINK_LABELS[key] || `Lien ${key}`,
      value: link,
    });
  });

  const fallbackLink = String(invitation?.referral_link || '').trim();
  if (fallbackLink && !seen.has(fallbackLink)) {
    items.unshift({
      key: 'default',
      label: INVITATION_LINK_LABELS.default,
      value: fallbackLink,
    });
  }

  return items;
};

const PaymentProfileForm = ({
  paymentProfile,
  setPaymentProfile,
  loadingPaymentProfile,
  savingPaymentProfile,
  onSubmit,
  allowWalletCredit = false,
}) => {
  const preferredPayoutMethod =
    !allowWalletCredit && ['wallet', 'wallet_credit'].includes(paymentProfile.preferred_payout_method)
      ? ''
      : paymentProfile.preferred_payout_method || '';

  return (
  <Card>
    <CardHeader>
      <CardTitle>Coordonnees de paiement</CardTitle>
      <CardDescription>
        Utilisees pour traiter les retraits de commissions sans recreer un second flux compte.
      </CardDescription>
    </CardHeader>
    <CardContent>
      {loadingPaymentProfile ? (
        <p className="text-sm text-slate-500">Chargement du profil de paiement...</p>
      ) : (
        <form className="grid grid-cols-1 xl:grid-cols-2 gap-4" onSubmit={onSubmit}>
          <div>
            <Label htmlFor="preferred-payout-method">Mode prefere</Label>
            <select
              id="preferred-payout-method"
              className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={preferredPayoutMethod}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  preferred_payout_method: event.target.value,
                }))
              }
            >
              <option value="">Aucun mode prefere</option>
              <option value="bank_transfer">Virement bancaire</option>
              <option value="mobile_money">Mobile money</option>
              {allowWalletCredit && <option value="wallet_credit">Portefeuille Coli Noki</option>}
            </select>
          </div>

          <div>
            <Label htmlFor="mobile-money-number">Numero mobile money</Label>
            <Input
              id="mobile-money-number"
              value={paymentProfile.mobile_money_number || ''}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  mobile_money_number: event.target.value,
                }))
              }
              placeholder="+242060000000"
            />
          </div>

          <div>
            <Label htmlFor="mobile-money-operator">Operateur mobile money</Label>
            <Input
              id="mobile-money-operator"
              value={paymentProfile.mobile_money_operator || ''}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  mobile_money_operator: event.target.value,
                }))
              }
              placeholder="Airtel Money / MTN MoMo"
            />
          </div>

          <div>
            <Label htmlFor="bank-account-name">Titulaire du compte</Label>
            <Input
              id="bank-account-name"
              value={paymentProfile.bank_account_name || ''}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  bank_account_name: event.target.value,
                }))
              }
              placeholder="Nom complet"
            />
          </div>

          <div>
            <Label htmlFor="bank-account-number">Numero du compte</Label>
            <Input
              id="bank-account-number"
              value={paymentProfile.bank_account_number || ''}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  bank_account_number: event.target.value,
                }))
              }
              placeholder="Numero de compte"
            />
          </div>

          <div>
            <Label htmlFor="bank-name">Banque</Label>
            <Input
              id="bank-name"
              value={paymentProfile.bank_name || ''}
              onChange={(event) =>
                setPaymentProfile((current) => ({
                  ...current,
                  bank_name: event.target.value,
                }))
              }
              placeholder="Nom de la banque"
            />
          </div>

          <div className="xl:col-span-2">
            <Button type="submit" disabled={savingPaymentProfile}>
              <Save className="mr-2 h-4 w-4" />
              {savingPaymentProfile ? 'Enregistrement...' : 'Enregistrer le profil de paiement'}
            </Button>
          </div>
        </form>
      )}
    </CardContent>
  </Card>
  );
};

const InvitationCard = ({ invitation, statusCode, onCopyLink, title = "Liens d'invitation", description }) => {
  const linkItems = buildInvitationLinkItems(invitation);

  return (
  <Card className="border-slate-200">
    <CardHeader>
      <CardTitle>{title}</CardTitle>
      <CardDescription>{description || 'Les liens publics restent centralises par le backend.'}</CardDescription>
    </CardHeader>
    <CardContent className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary">{invitation?.affiliate_code || '-'}</Badge>
        <StatusPill statusCode={statusCode} />
      </div>

      <div>
        <Label>Code affiliation</Label>
        <div className="mt-1 rounded-md bg-slate-100 px-3 py-2 font-mono text-sm">
          {invitation?.affiliate_code || '-'}
        </div>
      </div>

      {linkItems.length === 0 ? (
        <div>
          <Label>Lien partageable</Label>
          <div className="mt-1 flex gap-2">
            <Input value="" readOnly />
            <Button type="button" variant="outline" disabled data-testid="affiliate-copy-link">
              <Copy className="mr-2 h-4 w-4" />
              Copier
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {linkItems.map((item, index) => (
            <div key={`${item.key}-${item.value}`}>
              <Label>{item.label}</Label>
              <div className="mt-1 flex gap-2">
                <Input value={item.value} readOnly />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onCopyLink(item.value)}
                  disabled={!invitation?.can_copy}
                  data-testid={index === 0 ? 'affiliate-copy-link' : undefined}
                >
                  <Copy className="mr-2 h-4 w-4" />
                  Copier
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!invitation?.is_link_active && (
        <p className="text-xs text-amber-700">
          Le lien est visible mais inactif tant que le programme n'est pas actif.
        </p>
      )}
    </CardContent>
  </Card>
  );
};

const ReferralHistoryTable = ({ referrals }) => (
  <Card>
    <CardHeader>
      <CardTitle>Historique des inscriptions</CardTitle>
      <CardDescription>Vision claire des inscriptions generees, sans exposer de donnees sensibles.</CardDescription>
    </CardHeader>
    <CardContent>
      {referrals.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune inscription rattachee pour le moment.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Inscription</TableHead>
              <TableHead>Conversions</TableHead>
              <TableHead>Derniere activite</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {referrals.slice(0, 20).map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.source || '-'}</TableCell>
                <TableCell>{formatAffiliateDate(item.registered_at)}</TableCell>
                <TableCell>{Number(item.conversion_count || 0)}</TableCell>
                <TableCell>{formatAffiliateDate(item.last_conversion_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

const CommissionsTable = ({ commissions }) => (
  <Card>
    <CardHeader>
      <CardTitle>Historique des commissions</CardTitle>
      <CardDescription>Commissions calculees par le backend et deja dedoublonnees.</CardDescription>
    </CardHeader>
    <CardContent>
      {commissions.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune commission pour le moment.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Source</TableHead>
              <TableHead>Montant</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead>Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {commissions.slice(0, 25).map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <div className="font-medium">{item.source_type || '-'}</div>
                  <div className="text-xs text-slate-500">{item.source_id || '-'}</div>
                </TableCell>
                <TableCell>{formatAffiliateCurrency(item.amount, item.currency || 'FCFA')}</TableCell>
                <TableCell>
                  <Badge variant={getPayoutStatusVariant(item.status)}>{getPayoutStatusLabel(item.status)}</Badge>
                </TableCell>
                <TableCell>{formatAffiliateDate(item.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

const PayoutRequestsTable = ({ items }) => (
  <Card>
    <CardHeader>
      <CardTitle>Historique des retraits</CardTitle>
      <CardDescription>Suivi des gains en attente, disponibles ou deja payes.</CardDescription>
    </CardHeader>
    <CardContent>
      {items.length === 0 ? (
        <p className="text-sm text-slate-500">Aucune demande de paiement.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Montant</TableHead>
              <TableHead>Methode</TableHead>
              <TableHead>Statut</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.slice(0, 25).map((item) => (
              <TableRow key={item.id}>
                <TableCell>{formatAffiliateDate(item.request_date)}</TableCell>
                <TableCell>{formatAffiliateCurrency(item.amount, item.currency || 'FCFA')}</TableCell>
                <TableCell>{item.payment_method || '-'}</TableCell>
                <TableCell>
                  <Badge variant={getPayoutStatusVariant(item.status)}>{getPayoutStatusLabel(item.status)}</Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

const NetworkMembersTable = ({ members }) => (
  <Card>
    <CardHeader>
      <CardTitle>Reseau recrute</CardTitle>
      <CardDescription>Trace explicite des relations directes et indirectes retournees par le backend.</CardDescription>
    </CardHeader>
    <CardContent>
      {members.length === 0 ? (
        <p className="text-sm text-slate-500">Aucun membre reseau pour le moment.</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Relation</TableHead>
              <TableHead>Membre</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Conversions</TableHead>
              <TableHead>Inscription</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{NETWORK_RELATIONSHIP_LABELS[item.relationship] || item.relationship || '-'}</TableCell>
                <TableCell>
                  <div className="font-medium">{item.referred_user_name || '-'}</div>
                  <div className="text-xs text-slate-500">{item.referral_source_type || '-'}</div>
                </TableCell>
                <TableCell>{NETWORK_TARGET_LABELS[item.referral_target_type] || item.referral_target_type || '-'}</TableCell>
                <TableCell>{Number(item.conversion_count || 0).toLocaleString('fr-FR')}</TableCell>
                <TableCell>{formatAffiliateDateTime(item.registered_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
);

const SettingsShortcutCard = ({ navigate }) => (
  <Card>
    <CardHeader>
      <CardTitle>Parametres et securite</CardTitle>
      <CardDescription>
        Reutilise la page Parametres existante pour le mot de passe, le 2FA et les preferences.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-wrap gap-3">
      <Button variant="outline" onClick={() => navigate('/account/settings')}>
        <Settings className="mr-2 h-4 w-4" />
        Parametres du compte
      </Button>
      <Button variant="outline" onClick={() => navigate('/account/settings#security')}>
        <ShieldCheck className="mr-2 h-4 w-4" />
        Securite
      </Button>
    </CardContent>
  </Card>
);

const AffiliateDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submittingPayout, setSubmittingPayout] = useState(false);
  const [loadingPaymentProfile, setLoadingPaymentProfile] = useState(false);
  const [savingPaymentProfile, setSavingPaymentProfile] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [payoutMethod, setPayoutMethod] = useState('bank_transfer');
  const [paymentProfile, setPaymentProfile] = useState(DEFAULT_PAYMENT_PROFILE);

  const loadDashboard = async ({ withLoader = true, withErrorToast = true } = {}) => {
    if (withLoader) {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${API}/affiliate/dashboard`);
      setDashboard(response.data || null);
      return response.data || null;
    } catch (error) {
      if (withErrorToast) {
        toast.error(error?.response?.data?.detail || "Impossible de charger l'espace affiliation.");
      }
      if (error?.response?.status === 403) {
        navigate('/');
      }
      throw error;
    } finally {
      if (withLoader) {
        setLoading(false);
      }
    }
  };

  const loadPaymentProfile = async ({ withLoader = true } = {}) => {
    if (withLoader) {
      setLoadingPaymentProfile(true);
    }
    try {
      const response = await axios.get(`${API}/affiliate/payment-profile`);
      const profile = response?.data?.payment_profile || {};
      setPaymentProfile({
        preferred_payout_method: profile.preferred_payout_method || '',
        mobile_money_number: profile.mobile_money_number || '',
        mobile_money_operator: profile.mobile_money_operator || '',
        bank_account_name: profile.bank_account_name || '',
        bank_account_number: profile.bank_account_number || '',
        bank_name: profile.bank_name || '',
      });
      if (profile.preferred_payout_method) {
        setPayoutMethod(profile.preferred_payout_method);
      }
    } catch (error) {
      if (error?.response?.status !== 404) {
        toast.error(error?.response?.data?.detail || 'Impossible de charger le profil de paiement.');
      }
    } finally {
      if (withLoader) {
        setLoadingPaymentProfile(false);
      }
    }
  };

  useEffect(() => {
    loadDashboard();
    loadPaymentProfile();
  }, []);

  const affiliate = dashboard?.affiliate || {};
  const profile = dashboard?.profile || {};
  const invitation = dashboard?.invitation || {};
  const overview = dashboard?.overview || {};
  const performance = dashboard?.performance || {};
  const earnings = dashboard?.earnings || {};
  const commissionSummary = dashboard?.commission_summary || {};
  const commissions = dashboard?.commission_history || dashboard?.commissions || [];
  const referrals = dashboard?.referral_history || dashboard?.referrals || [];
  const network = dashboard?.network || {};
  const networkMembers = Array.isArray(network.members) ? network.members : [];
  const payoutRequests = dashboard?.payout_history || dashboard?.payout_requests || [];
  const programSettings = dashboard?.program_settings || {};
  const roleSections = dashboard?.role_sections || {};
  const dashboardKind = dashboard?.dashboard_kind || '';
  const affiliateType = normalizeAffiliateType(profile.affiliate_type || affiliate.affiliate_type || dashboard?.stats?.affiliate_type);
  const isSellerAffiliate = affiliateType === 'seller';
  const isSuperAmbassador =
    affiliateType === 'super_ambassador' || dashboardKind === 'super_ambassador_dashboard';
  const statusCode = profile?.status?.code || dashboard?.stats?.status_code || 'pending';
  const affiliateProgramActive = Boolean(
    profile?.program_active ??
      affiliate?.affiliate_program_active ??
      dashboard?.stats?.affiliate_program_active ??
      affiliate?.is_eligible
  );
  const allowWalletCredit = isSellerAffiliate;
  useEffect(() => {
    if (!allowWalletCredit && ['wallet', 'wallet_credit'].includes(payoutMethod)) {
      setPayoutMethod('bank_transfer');
    }
  }, [allowWalletCredit, payoutMethod]);
  const eligibilityMessage = profile?.eligibility_message || affiliate?.eligibility_message || dashboard?.stats?.eligibility_message || '';
  const minimumPayoutAmount = Number(earnings.minimum_payout_amount ?? programSettings.min_payout_amount ?? 500);
  const canRequestPayout = useMemo(() => {
    return Boolean(
      earnings.can_request_payout ??
        (programSettings.is_enabled &&
          affiliateProgramActive &&
          Number(earnings.available ?? dashboard?.stats?.available_commission ?? 0) >= minimumPayoutAmount)
    );
  }, [
    affiliateProgramActive,
    dashboard?.stats?.available_commission,
    earnings.available,
    earnings.can_request_payout,
    minimumPayoutAmount,
    programSettings.is_enabled,
  ]);

  const handleCopyLink = async (overrideLink) => {
    const link = overrideLink || invitation.referral_link || affiliate.referral_link;
    if (!link) {
      toast.error("Lien d'invitation indisponible.");
      return;
    }
    if (!affiliateProgramActive) {
      toast.error(eligibilityMessage || "Votre programme d'affiliation est inactif.");
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      toast.success('Lien copie.');
    } catch (error) {
      toast.error('Impossible de copier le lien.');
    }
  };

  const handleCreatePayoutRequest = async (event) => {
    event.preventDefault();
    if (submittingPayout) {
      return;
    }
    const amount = Number(payoutAmount || 0);
    if (!amount || amount <= 0) {
      toast.error('Montant invalide.');
      return;
    }

    setSubmittingPayout(true);
    try {
      await axios.post(`${API}/affiliate/payout-requests`, {
        amount,
        payment_method: payoutMethod,
      });
      toast.success("Demande de paiement envoyee avec succes.");
      setPayoutAmount('');
      await loadDashboard({ withLoader: false, withErrorToast: false });
    } catch (error) {
      toast.error(resolvePayoutRequestErrorMessage(error));
    } finally {
      setSubmittingPayout(false);
    }
  };

  const handleSavePaymentProfile = async (event) => {
    event.preventDefault();
    if (savingPaymentProfile) {
      return;
    }

    setSavingPaymentProfile(true);
    try {
      const response = await axios.patch(`${API}/affiliate/payment-profile`, {
        preferred_payout_method: paymentProfile.preferred_payout_method || null,
        mobile_money_number: paymentProfile.mobile_money_number || null,
        mobile_money_operator: paymentProfile.mobile_money_operator || null,
        bank_account_name: paymentProfile.bank_account_name || null,
        bank_account_number: paymentProfile.bank_account_number || null,
        bank_name: paymentProfile.bank_name || null,
      });
      const updatedProfile = response?.data?.data?.payment_profile || response?.data?.payment_profile || {};
      setPaymentProfile({
        preferred_payout_method: updatedProfile.preferred_payout_method || '',
        mobile_money_number: updatedProfile.mobile_money_number || '',
        mobile_money_operator: updatedProfile.mobile_money_operator || '',
        bank_account_name: updatedProfile.bank_account_name || '',
        bank_account_number: updatedProfile.bank_account_number || '',
        bank_name: updatedProfile.bank_name || '',
      });
      if (updatedProfile.preferred_payout_method) {
        setPayoutMethod(updatedProfile.preferred_payout_method);
      }
      toast.success('Profil de paiement enregistre.');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impossible de sauvegarder le profil de paiement.');
    } finally {
      setSavingPaymentProfile(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-red-500" />
          <p className="mt-3 text-sm text-slate-600">Chargement de votre espace affiliation...</p>
        </div>
      </div>
    );
  }

  const dashboardTitle =
    roleSections.headline ||
    (isSellerAffiliate
      ? 'Affiliation vendeur'
      : isSuperAmbassador
        ? 'Dashboard super ambassadeur'
        : 'Dashboard ambassadeur');
  const dashboardDescription =
    roleSections.focus ||
    (isSellerAffiliate
      ? 'Suivi de vos clics, inscriptions, conversions et commissions.'
      : isSuperAmbassador
        ? 'Pilotez votre reseau ambassadeur, vos vendeurs directs et les commissions a deux niveaux.'
        : 'Pilotez votre activite ambassadeur, vos activations vendeurs et vos commissions.');
  const backPath = getAffiliateBackPath(affiliateType);
  const backLabel = getAffiliateBackLabel(affiliateType);

  const sellerContent = (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <MetricCard
          title="Clics sur votre lien"
          value={Number(overview.clicks || dashboard?.stats?.clicks || 0).toLocaleString('fr-FR')}
          helper="Performance d'acquisition"
          icon={MousePointerClick}
        />
        <MetricCard
          title="Inscriptions generees"
          value={Number(overview.registrations || dashboard?.stats?.registrations || 0).toLocaleString('fr-FR')}
          helper={`${Number(performance.click_to_registration_rate || dashboard?.stats?.click_to_registration_rate || 0).toLocaleString('fr-FR')}% des clics`}
          icon={Users}
        />
        <MetricCard
          title="Conversions"
          value={Number(overview.conversions || dashboard?.stats?.conversions || 0).toLocaleString('fr-FR')}
          helper={`${Number(performance.conversion_rate || dashboard?.stats?.conversion_rate || 0).toLocaleString('fr-FR')}% des inscrits`}
          icon={TrendingUp}
        />
        <MetricCard
          title="Commissions disponibles"
          value={formatAffiliateCurrency(overview.available_commissions ?? dashboard?.stats?.available_commission ?? 0)}
          helper={`Total: ${formatAffiliateCurrency(overview.total_commissions ?? dashboard?.stats?.total_earnings ?? 0)}`}
          icon={Wallet}
          emphasis="text-emerald-700"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <InvitationCard invitation={invitation} statusCode={statusCode} onCopyLink={handleCopyLink} />
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Etat des gains</CardTitle>
            <CardDescription>Lecture rapide des montants en attente, disponibles et deja payes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
              <p className="text-xs uppercase tracking-wide text-amber-700">En attente</p>
              <p className="mt-2 text-2xl font-semibold text-amber-900">
                {formatAffiliateCurrency(commissionSummary.pending ?? dashboard?.stats?.commissions_pending ?? 0)}
              </p>
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
              <p className="text-xs uppercase tracking-wide text-emerald-700">Disponibles</p>
              <p className="mt-2 text-2xl font-semibold text-emerald-900">
                {formatAffiliateCurrency(commissionSummary.available ?? dashboard?.stats?.available_commission ?? 0)}
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs uppercase tracking-wide text-slate-600">Payes</p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatAffiliateCurrency(commissionSummary.paid ?? dashboard?.stats?.commissions_paid ?? 0)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Demander un retrait</CardTitle>
            <CardDescription>Minimum: {formatAffiliateCurrency(minimumPayoutAmount)}</CardDescription>
          </CardHeader>
          <CardContent>
            <form className="space-y-3" onSubmit={handleCreatePayoutRequest}>
              <div>
                <Label htmlFor="seller-payout-amount">Montant</Label>
                <Input
                  id="seller-payout-amount"
                  type="number"
                  min="1"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(event) => setPayoutAmount(event.target.value)}
                  disabled={!programSettings.is_enabled || !affiliateProgramActive}
                />
              </div>
              <div>
                <Label htmlFor="seller-payout-method">Methode</Label>
                <select
                  id="seller-payout-method"
                  className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={payoutMethod}
                  onChange={(event) => setPayoutMethod(event.target.value)}
                  disabled={!programSettings.is_enabled || !affiliateProgramActive}
                >
                  <option value="bank_transfer">Virement bancaire</option>
                  <option value="mobile_money">Mobile money</option>
                  {allowWalletCredit && <option value="wallet_credit">Portefeuille Coli Noki</option>}
                </select>
              </div>
              <Button type="submit" disabled={!canRequestPayout || submittingPayout} data-testid="seller-payout-submit">
                {submittingPayout ? 'Envoi...' : 'Envoyer la demande'}
              </Button>
              {!canRequestPayout && (
                <p className="text-xs text-amber-700">
                  {!affiliateProgramActive
                    ? eligibilityMessage || "Le programme d'affiliation est inactif."
                    : 'Le solde disponible est insuffisant ou le programme est desactive.'}
                </p>
              )}
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Lecture operationnelle</CardTitle>
            <CardDescription>Comprenez rapidement ou vous en etes dans votre acquisition.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <Users className="mt-0.5 h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">
                  {Number(overview.registrations || 0).toLocaleString('fr-FR')} inscription(s) generee(s)
                </p>
                <p className="text-sm text-slate-500">
                  Derniere inscription: {formatAffiliateDateTime(performance.last_registration_at)}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <TrendingUp className="mt-0.5 h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">
                  {Number(overview.conversions || 0).toLocaleString('fr-FR')} conversion(s) monetisees
                </p>
                <p className="text-sm text-slate-500">
                  Taux de conversion: {Number(performance.conversion_rate || 0).toLocaleString('fr-FR')}%
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
              <History className="mt-0.5 h-5 w-5 text-slate-500" />
              <div>
                <p className="font-medium text-slate-900">
                  {Number(overview.active_referred_vendors || 0).toLocaleString('fr-FR')} vendeur(s) actifs
                </p>
                <p className="text-sm text-slate-500">
                  Les historiques existants et les commissions deja calculees sont conserves.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ReferralHistoryTable referrals={referrals} />
        <CommissionsTable commissions={commissions} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <PayoutRequestsTable items={payoutRequests} />
        <PaymentProfileForm
          paymentProfile={paymentProfile}
          setPaymentProfile={setPaymentProfile}
          loadingPaymentProfile={loadingPaymentProfile}
          savingPaymentProfile={savingPaymentProfile}
          onSubmit={handleSavePaymentProfile}
          allowWalletCredit={allowWalletCredit}
        />
      </div>
    </div>
  );

  const ambassadorContent = (
    <Tabs defaultValue="overview" className="space-y-6">
      <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
        <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
        <TabsTrigger value="commissions">Commissions</TabsTrigger>
        <TabsTrigger value="invitation">Liens</TabsTrigger>
        <TabsTrigger value="settings">Parametres</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard
            title="Inscriptions generees"
            value={Number(overview.registrations || 0).toLocaleString('fr-FR')}
            helper="Base du pipeline ambassadeur"
            icon={Users}
          />
          <MetricCard
            title="Vendeurs actives"
            value={Number(overview.active_referred_vendors || 0).toLocaleString('fr-FR')}
            helper="Vendeurs deja actifs"
            icon={ShieldCheck}
          />
          <MetricCard
            title="Abonnements generes"
            value={Number(overview.subscriptions_generated || 0).toLocaleString('fr-FR')}
            helper={`${Number(performance.conversion_rate || 0).toLocaleString('fr-FR')}% de conversion`}
            icon={TrendingUp}
          />
          <MetricCard
            title="Commissions totales"
            value={formatAffiliateCurrency(overview.total_commissions || 0)}
            helper={`Disponibles: ${formatAffiliateCurrency(overview.available_commissions || 0)}`}
            icon={DollarSign}
            emphasis="text-cyan-700"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <InvitationCard invitation={invitation} statusCode={statusCode} onCopyLink={handleCopyLink} />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Performance</CardTitle>
              <CardDescription>Indicateurs mis en avant pour un vrai dashboard ambassadeur.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Taux clic vers inscription</p>
                <p className="mt-2 text-2xl font-semibold text-slate-900">
                  {Number(performance.click_to_registration_rate || 0).toLocaleString('fr-FR')}%
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Derniere inscription</p>
                <p className="mt-2 text-lg font-semibold text-slate-900">
                  {formatAffiliateDateTime(performance.last_registration_at)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-500">Structure future reseau</p>
                <p className="mt-2 text-sm text-slate-700">
                  Base prete pour super ambassadeurs, sans activer de hierarchie bancale maintenant.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Parent: {profile?.future_network?.parent_affiliate_id || 'Aucun'} | Niveau actuel:{' '}
                  {Number(profile?.future_network?.hierarchy_depth || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="commissions" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            title="En attente"
            value={formatAffiliateCurrency(commissionSummary.pending || 0)}
            helper="A confirmer ou traiter"
            icon={Clock3}
            emphasis="text-amber-700"
          />
          <MetricCard
            title="Disponibles"
            value={formatAffiliateCurrency(commissionSummary.available || 0)}
            helper="Retirables"
            icon={Wallet}
            emphasis="text-emerald-700"
          />
          <MetricCard
            title="Payees"
            value={formatAffiliateCurrency(commissionSummary.paid || 0)}
            helper="Deja versees"
            icon={DollarSign}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <CommissionsTable commissions={commissions} />
          <PayoutRequestsTable items={payoutRequests} />
        </div>
      </TabsContent>

      <TabsContent value="invitation" className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <InvitationCard invitation={invitation} statusCode={statusCode} onCopyLink={handleCopyLink} />
          <ReferralHistoryTable referrals={referrals} />
        </div>
      </TabsContent>

      <TabsContent value="settings" className="space-y-6">
        <SettingsShortcutCard navigate={navigate} />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Demande de paiement</CardTitle>
              <CardDescription>Minimum: {formatAffiliateCurrency(minimumPayoutAmount)}</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-3" onSubmit={handleCreatePayoutRequest}>
                <div>
                  <Label htmlFor="ambassador-payout-amount">Montant</Label>
                  <Input
                    id="ambassador-payout-amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={payoutAmount}
                    onChange={(event) => setPayoutAmount(event.target.value)}
                    disabled={!programSettings.is_enabled || !affiliateProgramActive}
                  />
                </div>
                <div>
                  <Label htmlFor="ambassador-payout-method">Methode</Label>
                  <select
                    id="ambassador-payout-method"
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={payoutMethod}
                    onChange={(event) => setPayoutMethod(event.target.value)}
                    disabled={!programSettings.is_enabled || !affiliateProgramActive}
                  >
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="mobile_money">Mobile money</option>
                    {allowWalletCredit && <option value="wallet_credit">Portefeuille Coli Noki</option>}
                  </select>
                </div>
                <Button type="submit" disabled={!canRequestPayout || submittingPayout} data-testid="ambassador-payout-submit">
                  {submittingPayout ? 'Envoi...' : 'Envoyer la demande'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <PaymentProfileForm
            paymentProfile={paymentProfile}
            setPaymentProfile={setPaymentProfile}
            loadingPaymentProfile={loadingPaymentProfile}
            savingPaymentProfile={savingPaymentProfile}
            onSubmit={handleSavePaymentProfile}
            allowWalletCredit={allowWalletCredit}
          />
        </div>
      </TabsContent>
    </Tabs>
  );

  const superAmbassadorContent = (
    <Tabs defaultValue="overview" className="space-y-6">
      <TabsList className="grid w-full grid-cols-2 md:grid-cols-5">
        <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
        <TabsTrigger value="commissions">Commissions</TabsTrigger>
        <TabsTrigger value="network">Reseau</TabsTrigger>
        <TabsTrigger value="invitation">Liens</TabsTrigger>
        <TabsTrigger value="settings">Parametres</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
          <MetricCard
            title="Ambassadeurs recrutes"
            value={Number(overview.ambassadors_recruited || 0).toLocaleString('fr-FR')}
            helper={`${Number(overview.active_ambassadors || 0).toLocaleString('fr-FR')} actifs`}
            icon={Users}
          />
          <MetricCard
            title="Vendeurs directs"
            value={Number(overview.direct_vendors || 0).toLocaleString('fr-FR')}
            helper="Recrutes sans intermediaire"
            icon={ShieldCheck}
          />
          <MetricCard
            title="Vendeurs indirects"
            value={Number(overview.indirect_vendors || 0).toLocaleString('fr-FR')}
            helper="Passes par un ambassadeur du reseau"
            icon={History}
          />
          <MetricCard
            title="Premiers abonnements Pro"
            value={Number(overview.first_subscriptions_generated || 0).toLocaleString('fr-FR')}
            helper="Abonnements commissionnables deja traces"
            icon={TrendingUp}
          />
          <MetricCard
            title="Commissions totales"
            value={formatAffiliateCurrency(overview.total_commissions || 0)}
            helper={`Disponibles: ${formatAffiliateCurrency(overview.available_commissions || 0)}`}
            icon={DollarSign}
            emphasis="text-violet-700"
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div className="xl:col-span-2">
            <InvitationCard
              invitation={invitation}
              statusCode={statusCode}
              onCopyLink={handleCopyLink}
              title="Liens d'invitation super ambassadeur"
              description="Le backend distingue le recrutement direct de vendeurs et le recrutement d ambassadeurs."
            />
          </div>
          <Card>
            <CardHeader>
              <CardTitle>Commissions reseau</CardTitle>
              <CardDescription>Lecture separee entre direct, indirect et etats de paiement.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
                <p className="text-xs uppercase tracking-wide text-violet-700">Directes</p>
                <p className="mt-2 text-2xl font-semibold text-violet-900">
                  {formatAffiliateCurrency(overview.direct_commissions || 0)}
                </p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-4">
                <p className="text-xs uppercase tracking-wide text-cyan-700">Indirectes</p>
                <p className="mt-2 text-2xl font-semibold text-cyan-900">
                  {formatAffiliateCurrency(overview.indirect_commissions || 0)}
                </p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs uppercase tracking-wide text-slate-600">En attente / disponibles / payees</p>
                <p className="mt-2 text-sm font-semibold text-slate-900">
                  {formatAffiliateCurrency(commissionSummary.pending || 0)} / {formatAffiliateCurrency(commissionSummary.available || 0)} /{' '}
                  {formatAffiliateCurrency(commissionSummary.paid || 0)}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="commissions" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard
            title="Commissions directes"
            value={formatAffiliateCurrency(overview.direct_commissions || 0)}
            helper="Vendeurs directs du super ambassadeur"
            icon={DollarSign}
            emphasis="text-violet-700"
          />
          <MetricCard
            title="Commissions indirectes"
            value={formatAffiliateCurrency(overview.indirect_commissions || 0)}
            helper="Reteau ambassadeur"
            icon={TrendingUp}
            emphasis="text-cyan-700"
          />
          <MetricCard
            title="Disponibles"
            value={formatAffiliateCurrency(commissionSummary.available || 0)}
            helper="Retirables"
            icon={Wallet}
            emphasis="text-emerald-700"
          />
          <MetricCard
            title="Payees"
            value={formatAffiliateCurrency(commissionSummary.paid || 0)}
            helper="Deja versees"
            icon={Clock3}
          />
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <CommissionsTable commissions={commissions} />
          <PayoutRequestsTable items={payoutRequests} />
        </div>
      </TabsContent>

      <TabsContent value="network" className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          <MetricCard
            title="Ambassadeurs actifs"
            value={Number(network.active_ambassadors || 0).toLocaleString('fr-FR')}
            helper={`${Number(network.direct_ambassadors || 0).toLocaleString('fr-FR')} recrutes au total`}
            icon={Users}
          />
          <MetricCard
            title="Vendeurs directs"
            value={Number(network.direct_vendors || 0).toLocaleString('fr-FR')}
            helper="Recrutement direct"
            icon={ShieldCheck}
          />
          <MetricCard
            title="Vendeurs indirects"
            value={Number(network.indirect_vendors || 0).toLocaleString('fr-FR')}
            helper="Par ambassadeur intermediaire"
            icon={History}
          />
          <MetricCard
            title="Premiers abonnements"
            value={Number(network.first_subscriptions_generated || 0).toLocaleString('fr-FR')}
            helper="Seulement le premier abonnement Pro"
            icon={TrendingUp}
          />
        </div>

        <NetworkMembersTable members={networkMembers} />
      </TabsContent>

      <TabsContent value="invitation" className="space-y-6">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <InvitationCard
            invitation={invitation}
            statusCode={statusCode}
            onCopyLink={handleCopyLink}
            title="Liens reseau"
            description="Utilisez le bon lien selon que vous recrutez un ambassadeur ou un vendeur direct."
          />
          <Card>
            <CardHeader>
              <CardTitle>Regles reseau</CardTitle>
              <CardDescription>Hierarchie explicite et tracable cote backend.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600">
              <p>Direct vendeur: 50% sur le premier abonnement Pro du vendeur recrute directement.</p>
              <p>Indirect reseau: 25% quand un ambassadeur de votre reseau recrute un vendeur qui prend son premier abonnement Pro.</p>
              <p>Le backend verrouille la source, bloque les doublons et ne commissionne pas les renouvellements.</p>
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="settings" className="space-y-6">
        <SettingsShortcutCard navigate={navigate} />

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Demande de paiement</CardTitle>
              <CardDescription>Minimum: {formatAffiliateCurrency(minimumPayoutAmount)}</CardDescription>
            </CardHeader>
            <CardContent>
              <form className="space-y-3" onSubmit={handleCreatePayoutRequest}>
                <div>
                  <Label htmlFor="super-ambassador-payout-amount">Montant</Label>
                  <Input
                    id="super-ambassador-payout-amount"
                    type="number"
                    min="1"
                    step="0.01"
                    value={payoutAmount}
                    onChange={(event) => setPayoutAmount(event.target.value)}
                    disabled={!programSettings.is_enabled || !affiliateProgramActive}
                  />
                </div>
                <div>
                  <Label htmlFor="super-ambassador-payout-method">Methode</Label>
                  <select
                    id="super-ambassador-payout-method"
                    className="mt-1 h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={payoutMethod}
                    onChange={(event) => setPayoutMethod(event.target.value)}
                    disabled={!programSettings.is_enabled || !affiliateProgramActive}
                  >
                    <option value="bank_transfer">Virement bancaire</option>
                    <option value="mobile_money">Mobile money</option>
                    {allowWalletCredit && <option value="wallet_credit">Portefeuille Coli Noki</option>}
                  </select>
                </div>
                <Button type="submit" disabled={!canRequestPayout || submittingPayout} data-testid="super-ambassador-payout-submit">
                  {submittingPayout ? 'Envoi...' : 'Envoyer la demande'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <PaymentProfileForm
            paymentProfile={paymentProfile}
            setPaymentProfile={setPaymentProfile}
            loadingPaymentProfile={loadingPaymentProfile}
            savingPaymentProfile={savingPaymentProfile}
            onSubmit={handleSavePaymentProfile}
            allowWalletCredit={allowWalletCredit}
          />
        </div>
      </TabsContent>
    </Tabs>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 md:px-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">{getAffiliateTypeLabel(affiliateType)}</Badge>
              <StatusPill statusCode={statusCode} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{dashboardTitle}</h1>
              <p className="mt-1 max-w-3xl text-sm text-slate-600">{dashboardDescription}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => navigate('/account/settings')}>
              <Settings className="mr-2 h-4 w-4" />
              Parametres
            </Button>
            <Button variant="outline" onClick={() => navigate(backPath)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {backLabel}
            </Button>
          </div>
        </div>

        {!programSettings.is_enabled && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="pt-6 text-sm text-amber-900">
              Le programme d'affiliation est actuellement desactive par l'administration.
            </CardContent>
          </Card>
        )}

        {Boolean(eligibilityMessage) && !affiliateProgramActive && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="pt-6 text-sm text-amber-900">{eligibilityMessage}</CardContent>
          </Card>
        )}

        <Card className="border-slate-200 bg-white">
          <CardContent className="flex flex-col gap-4 pt-6 md:flex-row md:items-center md:justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-slate-700">Programme actuel</p>
              <p className="text-sm text-slate-500">
                {isSellerAffiliate
                  ? 'Affiliation vendeur distincte du parcours ambassadeur.'
                  : isSuperAmbassador
                    ? 'Espace super ambassadeur distinct de l ambassadeur standard et de l affiliation vendeur.'
                    : 'Espace ambassadeur separe du programme vendeur pour une lecture plus claire.'}
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3 md:flex md:items-center">
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Code</p>
                <p className="mt-1 font-mono text-sm font-semibold text-slate-900">{invitation.affiliate_code || '-'}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Derniere activite</p>
                <p className="mt-1 text-sm font-semibold text-slate-900">
                  {formatAffiliateDateTime(performance.last_registration_at || performance.last_conversion_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <SectionHeading
          title={
            isSellerAffiliate
              ? 'Vue affiliation vendeur'
              : isSuperAmbassador
                ? 'Vue super ambassadeur'
                : 'Vue ambassadeur'
          }
          description={
            isSellerAffiliate
              ? 'Le vendeur comprend rapidement ses clics, inscriptions, gains et retraits.'
              : isSuperAmbassador
                ? 'Le super ambassadeur suit clairement ses ambassadeurs recrutes, ses vendeurs directs, ses vendeurs indirects et ses commissions reseau.'
                : 'L ambassadeur dispose d un vrai dashboard structure avec performance, commissions, liens et acces securite.'
          }
        />

        {isSellerAffiliate ? sellerContent : isSuperAmbassador ? superAmbassadorContent : ambassadorContent}
      </div>
    </div>
  );
};

export default AffiliateDashboard;
